import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { router, usePage } from '@inertiajs/react'
import {
  Key,
  Plus,
  Shield,
  ShieldOff,
  Clock,
  CheckCircle,
  XCircle,
  Copy,
  AlertTriangle,
  Activity,
  Users
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface ApiKeyItem {
  id: number
  name: string
  description: string | null
  key_prefix: string
  active: boolean
  expired: boolean
  usable: boolean
  expires_at: string | null
  last_used_at: string | null
  requests_count: number
  created_at: string
}

interface AdminAccount {
  id: number
  name: string
  email: string
  community_name: string
  community_slug: string
}

interface Stats {
  total: number
  active: number
  expired: number
}

interface PageProps {
  api_keys: ApiKeyItem[]
  admin_accounts: AdminAccount[]
  stats: Stats
  flash?: { notice?: string; alert?: string }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatDateShort(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

export default function Index({ api_keys, admin_accounts, stats }: PageProps) {
  const { props } = usePage<{ flash?: { notice?: string; alert?: string }; new_key?: string; new_key_name?: string }>()
  const newKey = new URLSearchParams(window.location.search).get('new_key')
  const newKeyName = new URLSearchParams(window.location.search).get('new_key_name')

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [copied, setCopied] = useState<string | null>(null)

  const handleCreate = useCallback(() => {
    if (!name.trim()) return
    router.post('/admin/api_keys', {
      api_key: { name: name.trim(), description: description.trim() || null }
    }, {
      onSuccess: () => {
        setName('')
        setDescription('')
        setShowCreateForm(false)
      }
    })
  }, [name, description])

  const handleRevoke = useCallback((apiKey: ApiKeyItem) => {
    if (confirm(`Voulez-vous vraiment revoquer la cle API "${apiKey.name}" ? Cette action est irreversible.`)) {
      router.post(`/admin/api_keys/${apiKey.id}/revoke`)
    }
  }, [])

  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
  }, [])

  return (
    <AdminLayout
      title="Cles API"
      breadcrumbs={[{ label: 'Cles API' }]}
    >
      {/* Nouvelle cle generee */}
      {newKey && (
        <Card className="p-4 mb-6 border-2 border-yellow-400 bg-yellow-50">
          <div className="flex items-start gap-3">
            <AlertTriangle size={24} className="text-yellow-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-yellow-800 mb-1">
                Cle API "{newKeyName}" creee avec succes
              </h3>
              <p className="text-sm text-yellow-700 mb-3">
                Copiez cette cle maintenant. Elle ne sera plus jamais affichee.
              </p>
              <div className="flex items-center gap-2 bg-white rounded-lg border border-yellow-300 p-3">
                <code className="flex-1 text-sm font-mono text-gray-900 break-all select-all">
                  {newKey}
                </code>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyToClipboard(newKey, 'key')}
                >
                  {copied === 'key' ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} />}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Key size={24} className="text-gray-400" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray">Actives</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
            <Shield size={24} className="text-green-600" />
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-text-gray">Expirees</p>
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            </div>
            <ShieldOff size={24} className="text-red-600" />
          </div>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-bold text-gray-900">Cles API</h2>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-2"
        >
          <Plus size={18} />
          Nouvelle cle
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="p-4 mb-6 border-2 border-tessan-green/20">
          <h3 className="font-bold text-gray-900 mb-4">Creer une nouvelle cle API</h3>
          <div className="space-y-4">
            <Input
              label="Nom"
              placeholder="Ex: Bot moderateur communaute"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-text-primary">Description</label>
              <textarea
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tessan-green focus:border-transparent transition-colors"
                placeholder="Description optionnelle..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreate} disabled={!name.trim()}>
                Creer la cle
              </Button>
              <Button variant="secondary" onClick={() => setShowCreateForm(false)}>
                Annuler
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* API Keys Table */}
      <Card className="overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Nom</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Prefixe</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Statut</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Utilisation</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Derniere utilisation</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Creee le</th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {api_keys.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <Key size={32} className="text-gray-300" />
                      Aucune cle API
                    </div>
                  </td>
                </tr>
              ) : (
                api_keys.map((apiKey) => (
                  <tr key={apiKey.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <span className="font-medium text-text-dark">{apiKey.name}</span>
                        {apiKey.description && (
                          <p className="text-xs text-text-gray mt-0.5">{apiKey.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-sm bg-gray-100 px-2 py-0.5 rounded font-mono">
                        {apiKey.key_prefix}...
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      {apiKey.usable ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle size={12} />
                          Active
                        </span>
                      ) : apiKey.expired ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          <Clock size={12} />
                          Expiree
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          <XCircle size={12} />
                          Revoquee
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-text-gray">
                        <Activity size={14} />
                        {apiKey.requests_count.toLocaleString('fr-FR')} requetes
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Jamais'}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {formatDateShort(apiKey.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        {apiKey.active && !apiKey.expired && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRevoke(apiKey)}
                          >
                            Revoquer
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Admin Accounts Reference */}
      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
        <Users size={20} />
        Comptes admin par communaute
      </h2>
      <p className="text-sm text-text-gray mb-4">
        IDs a utiliser avec le header <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">X-Acting-As-User-ID</code> pour le bot moderateur.
      </p>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-text-gray">ID</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Nom</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Email</th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">Communaute</th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">Copier</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {admin_accounts.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-text-gray">
                    Aucun compte admin trouve
                  </td>
                </tr>
              ) : (
                admin_accounts.map((account) => (
                  <tr key={account.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <code className="text-sm bg-gray-100 px-2 py-0.5 rounded font-mono font-bold">
                        {account.id}
                      </code>
                    </td>
                    <td className="px-4 py-3 font-medium text-text-dark">
                      {account.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {account.email}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        account.community_slug === 'medecins'
                          ? 'bg-blue-100 text-blue-800'
                          : account.community_slug === 'pharmaciens'
                          ? 'bg-purple-100 text-purple-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {account.community_name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => copyToClipboard(String(account.id), `id-${account.id}`)}
                          className="flex items-center gap-1"
                        >
                          {copied === `id-${account.id}` ? (
                            <><CheckCircle size={14} className="text-green-600" /> Copie</>
                          ) : (
                            <><Copy size={14} /> ID</>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Usage Example */}
      <Card className="p-4 mt-6 bg-gray-50">
        <h3 className="font-bold text-gray-900 mb-3">Exemple d'utilisation</h3>
        <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
          <pre className="text-sm text-green-400 font-mono whitespace-pre">
{`curl -X GET https://community.tessan.io/api/v1/posts \\
  -H "X-Api-Key: tsn_votre_cle_api" \\
  -H "X-Acting-As-User-ID: ID_ADMIN"`}
          </pre>
        </div>
      </Card>
    </AdminLayout>
  )
}
